<!--
  PURPOSE LINKS (June '26) — the /collect "Purpose" selector.

  Lets the owner aim a collect channel (QR/link, email invites, SMS invites, social) at a
  specific goal: General Feedback (the normal survey), Review Request (one share step,
  optionally narrowed to picked platforms) or Referral Request (one refer-a-friend step).
  The selection lives in the Vuex store (collectPurpose) so it persists as the user moves
  between the collect pages. Blocking states surface here AND via the dash/purposeBlocked
  getter, which the pages use to disable their send/copy actions.
-->
<template>
  <div class="purposeselect mt-6 mb-8">
    <h6 class="font-medium text-light mb-2">PURPOSE
      <vx-tooltip class="ml-1 inline-block" position="right"
        text="What should this link ask your customers for? 'Review Request' and 'Referral Request' show just that one step instead of your full survey.">
        <small class="flex cursor-pointer">
          <feather-icon icon="HelpCircleIcon" svgClasses="w-4 h-4 text-light"></feather-icon>
        </small>
      </vx-tooltip>
    </h6>
    <v-select class="w-full bg-white ptlargerselect" :searchable="false" :clearable="false"
      :options="purposeOptions" label="label" :reduce="option => option.value" v-model="purpose" />

    <!-- review: narrow to specific platforms (none ticked = all connected platforms) -->
    <div v-if="purpose === 'review' && platformOptions.length > 0" class="mt-4">
      <h6 class="font-medium text-light mb-2">PLATFORMS <span class="font-normal">(leave all unticked to offer every platform)</span></h6>
      <ul class="flex flex-wrap justify-left">
        <li v-for="option in platformOptions" :key="option.code" class="mr-4 my-1 flex items-center">
          <vs-checkbox :vs-value="option.code" class="ml-0" v-model="platforms">{{ option.label }}</vs-checkbox>
        </li>
      </ul>
    </div>

    <!-- review with nothing to review on -->
    <div v-if="purpose === 'review' && platformOptions.length === 0" class="p-3 mt-4"
      style="background:#fff4e5;border-radius:6px;color:#b26a00;font-size:13px">
      You haven't connected any review platforms yet, so a Review Request can't be created.
      Connect your review profiles under
      <router-link to="/dash/settings/sharingandreviews" class="font-semibold" style="color:#b26a00;text-decoration:underline">Settings &rsaquo; Sharing &amp; Reviews</router-link>.
    </div>

    <!-- referral with the program off -->
    <div v-if="purpose === 'referral' && !referralProgramEnabled" class="p-3 mt-4"
      style="background:#fff4e5;border-radius:6px;color:#b26a00;font-size:13px">
      Your referral program is currently <strong>off</strong>, so a Referral Request can't be sent.
      Turn it on under
      <router-link to="/dash/settings/referrals" class="font-semibold" style="color:#b26a00;text-decoration:underline">Settings &rsaquo; Referrals</router-link>.
    </div>
  </div>
</template>

<script>
//vue-select is NOT registered globally (globalComponents.js's Vue.component(vSelect) misses the
//name argument, making it a getter call) — every consumer registers it locally, like the rest
import vSelect from 'vue-select'

export default {
  components: { vSelect },
  data () {
    return {
      purposeOptions: [
        { label: 'General Feedback', value: 'general' },
        { label: 'Review Request', value: 'review' },
        { label: 'Referral Request', value: 'referral' }
      ]
    }
  },
  computed: {
    purpose: {
      get () {
        return this.$store.state.dash.collectPurpose.purpose
      },
      set (v) {
        this.$store.commit('dash/SET_COLLECT_PURPOSE', {
          purpose: v,
          //platform picks only make sense for review — reset them on any purpose change
          platforms: []
        })
      }
    },
    platforms: {
      get () {
        return this.$store.state.dash.collectPurpose.platforms
      },
      set (v) {
        this.$store.commit('dash/SET_COLLECT_PURPOSE', {
          purpose: this.$store.state.dash.collectPurpose.purpose,
          platforms: v
        })
      }
    },
    platformOptions () {
      return this.$store.getters['dash/purposePlatformOptions']
    },
    referralProgramEnabled () {
      return this.$store.getters['dash/referralProgramEnabled']
    }
  }
}
</script>

<style lang="scss">
//unscoped on purpose (scoped attrs don't reach vue-select's internal nodes) — namespaced
//under .purposeselect. Don't rely on Allfeedback.vue's .ptlargerselect: that block only
//loads with its route chunk, so it isn't reliably present on the collect pages.
.purposeselect {
  .vs__dropdown-toggle {
    padding: 8px 10px;
  }
  .vs__selected {
    font-size: 1.25rem;
    font-weight: 500;
    padding: 4px 6px;
  }
  .vs__dropdown-menu li,
  .vs__dropdown-option {
    font-size: 1.15rem;
    padding: 9px 14px;
  }
}
</style>
